import { RETIRED_TRANSCRIPTION_ENGINES } from '../utils/speechmatics/engines';

/**
 * Which language a transcript was run in.
 *
 * The language is not recorded on a transcript, only the engine that produced
 * it, and Speechmatics ships one engine per language — so the engine id read
 * back through the per-language map IS the language. Callers that need to act
 * on a transcript's language (speaker separation, which is handed that
 * transcript's audio) go through here rather than assuming the language
 * currently selected for the next run.
 *
 * An engine id the current table does not know is looked up in
 * `RETIRED_TRANSCRIPTION_ENGINES` before giving up, because the table moves:
 * when the default went to v14.13.0 every transcript already in the estate
 * was produced by an id that had just left it. Callers must still handle
 * `undefined` — the retired table covers the engines Veritone shipped, not
 * one an org configured itself.
 *
 * Whether separation can then be OFFERED for that language is a different
 * question, and deliberately not answered here: it depends on which engines
 * this environment has deployed, which is state, not a pure mapping. See
 * `selectSpeakerSeparationLanguageSupported` in
 * `common-app/state/modules/engines/selectors.ts` — the one answer, for the
 * picker, the transcript tab and the run paths alike. A map-only
 * `supportsSpeakerSeparation` used to live here and was the bug: the ids are
 * built into the app, so it said yes in every environment and let jobs start
 * on engines the cluster did not have (VE-18990).
 */
export const languageOfTranscriptionEngine = (
  transcriptionEngines: Record<string, string> | null | undefined,
  engineId: string | undefined
): string | undefined => {
  if (!engineId) {
    return undefined;
  }
  const configured = transcriptionEngines
    ? Object.keys(transcriptionEngines).find(
        (code) => transcriptionEngines[code] === engineId
      )
    : undefined;
  // The configured table wins: an org that repoints a language has repointed
  // it, and the retired table is only there to name what we no longer run.
  return configured ?? RETIRED_TRANSCRIPTION_ENGINES[engineId];
};
